import { TRIP_STATUS } from '@/shared/common';
import { differenceInCalendarDays } from 'date-fns';

const isTripInvoicePending = (trip) => {
  if (!trip?.assignments?.length) return true;
  return trip?.assignments?.some((assignment) => assignment.invoiceStatus === 'pending');
};

export const getters = {
  tripRequests(state) {
    return state.tripRequests;
  },
  tripRequestsById(state) {
    const byId = {};
    state.tripRequests.forEach((e) => (byId[e.id] = e));
    return byId;
  },
  currentTripRequestApprovalStatus(state) {
    if (!state.currentTripRequest) return null;

    const { approval, assignments, status } = state.currentTripRequest;
    const historyApproved = approval?.history.filter((h) => h.approved === 1);
    const isFullyApproved =
      historyApproved?.length === approval?.requiredApprovalLevels?.length && status !== TRIP_STATUS.DRAFT;
    const isPartiallyApproved = !!historyApproved?.length;
    const isAutoApproved =
      approval.approved && approval.requiredApprovalLevels.length === 0 && status !== TRIP_STATUS.DRAFT;
    const isAssigned = assignments?.length > 0;

    if (status === 0) return 'pending';
    if (isAutoApproved) return 'auto';
    if (isFullyApproved) return 'full';
    if (isPartiallyApproved) return 'partial';
    if (isAssigned) return 'assigned';

    return 'pending';
  },
  currentTripRequestPermissions(state) {
    if (!state.currentTripRequest) return null;

    return Object.keys(state.currentTripRequest.permissions);
  },
  isCurrentTripRequestApproved(state, getters) {
    const status = getters.currentTripRequestApprovalStatus;
    return ['auto', 'full', 'partial', 'assigned'].includes(status);
  },
  isFieldReadonlyAfterApproval(state, getters, rootState, rootGetters) {
    if (!state.currentTripRequest) return false;

    const isAdmin = rootGetters['user/isAdmin'];
    if (isAdmin) return false;

    const me = rootState.user.me;
    const editPermissions = [
      'canEdit',
      'canEditMidApproval',
      'canEditPreApproval',
      'canEditApproved',
      'canEditSiteAdmin',
    ];

    const isApproved = getters.isCurrentTripRequestApproved;

    if (!isApproved) return false;

    const permissions = getters.currentTripRequestPermissions;

    const hasEditPermission = editPermissions.some((permission) => permissions.includes(permission));

    const currentLevel = state.currentTripRequest.approval?.requiredApprovalLevels?.find(
      (ral) => ral.status === 'pending'
    );
    const isCurrentLevelApprover = currentLevel?.approvers?.includes(me.id);

    if (!isCurrentLevelApprover) return true;

    const { approver: approverConfig } = rootGetters['config/permissions'];
    if (isCurrentLevelApprover && !approverConfig.allowEditPendingApproval) return true;

    if (me.is.approver && hasEditPermission) return getters.readOnlyBasedOnConfig;

    if (isApproved && hasEditPermission) return false;

    return true;
  },
  isTripApprovedAndSubmittedByRequester(state, getters, rootState, rootGetters) {
    if (!state.currentTripRequest) return false;
    const isApproved = getters.isCurrentTripRequestApproved;
    if (!isApproved) return false;

    const me = rootState.user.me;
    const isAdmin = rootGetters['user/isAdmin'];
    if (isAdmin) return false;
    if (isApproved && me.is.requester) return true;

    return false;
  },
  currentTripRequest(state) {
    return state.currentTripRequest;
  },
  currentTripLocation(state) {
    return state.currentTripLocation;
  },
  currentTripTypeId(state) {
    return state.currentTripTypeId;
  },
  currentTripFundingSources(state) {
    return state.currentFundingSources || [];
  },
  isCurrentTripFundingNoBudgetCode(state, getters, rootState, rootGetters) {
    return ({ fundingSourceIndex }) => {
      const currentTripRequest = state.currentTripRequest;

      if (!currentTripRequest) return false;

      const tripFundingSource = currentTripRequest.fundingSources[fundingSourceIndex];
      const fundingSourcesById = rootGetters['fundingSource/fundingSourcesById'];
      const fundingSource = fundingSourcesById[tripFundingSource.fundingSourceId];

      if (fundingSource.type !== 1) return false;

      if (!currentTripRequest) return false;

      const isTripNoBudgetCode = !tripFundingSource.budgetCodeId && !tripFundingSource.budgetCode;
      const isFundingSourceNoBudgetCode = !fundingSource.budgetCodeId;

      const hasNoBudgetCode = isTripNoBudgetCode || isFundingSourceNoBudgetCode;
      const isFundingSourceEditable = fundingSource.type === 4;

      if (hasNoBudgetCode && !isFundingSourceEditable) return true;

      return false;
    };
  },
  isCurrentTripRequester(state, getters, rootState, rootGetters) {
    const me = rootGetters['user/me'];

    return Number(state.currentTripRequest.createdBy) === me.id;
  },
  canApproveCurrentTrip(state, getters, rootState, rootGetters) {
    const me = rootGetters['user/me'];
    const isAdmin = rootGetters['user/isAdmin'];

    const tripApproval = state.currentTripRequest?.approval || null;

    if (!tripApproval) return false;

    const hasApprovers = tripApproval.requiredApprovalLevels?.length > 0;

    if (isAdmin) return true;
    if (getters.isCurrentTripRequester) return true;
    if (!hasApprovers && me.is.approver) return true;
    if (me.is.siteAdmin && state.currentTripRequest.approval.requiredApprovalLevels.length === 1) return true;

    // Site admin can always approve first level
    const firstLevel = tripApproval?.requiredApprovalLevels?.[0];
    if (me.is.siteAdmin && firstLevel?.status === 'pending') return true;

    const level = tripApproval.awaitingApproval?.id;
    const levelApprovers = [
      ...(tripApproval.awaitingApproval?.primaryAprovers || []),
      ...(tripApproval.awaitingApproval?.secondaryApprovers || []),
    ].reduce((acc, approver) => {
      if (approver?.approvalLevelId === level) {
        acc.push(approver?.userId);
      }
      return acc;
    }, []);

    return levelApprovers?.includes(me.id);
  },
  isLoading(state) {
    return state.isLoading;
  },

  isCurrentTripSubmittedOrDenied(state) {
    if (!state?.currentTripRequest?.status) return false;

    return [TRIP_STATUS.SUBMITTED, TRIP_STATUS.DENIED].includes(state.currentTripRequest.status);
  },

  allowTripDuplicate(state, getters, rootState) {
    if (!state.currentTripRequest) return false;

    return rootState.config.tripRequestConfig.display.duplicate && getters.isCurrentTripSubmittedOrDenied;
  },

  approveText(state) {
    if (!state.currentTripRequest?.approval?.awaitingApproval) return '';

    const { name, fundingSourceName } = state.currentTripRequest.approval.awaitingApproval;
    const approvalName = fundingSourceName ? `${name} - ${fundingSourceName}` : name;

    return approvalName ? `${approvalName} Approval` : '';
  },

  readOnlyBasedOnPermissions(state) {
    if (!state.currentTripRequest || !state.currentTripRequest.status) return false;
    const { permissions } = state.currentTripRequest;

    return (
      Object.keys(permissions).length &&
      (!permissions.canEdit ||
        permissions.canEditPreApproval === false ||
        permissions.canEditMidApproval === false ||
        permissions.canEditApproved === false)
    );
  },

  readOnlyBasedOnConfig(state, getters, rootState, rootGetters) {
    if (!state.currentTripRequest) return false;
    const me = rootGetters['user/me'];
    const isAdmin = rootGetters['user/isAdmin'];

    // nothing is readonly for admin
    if (isAdmin) return false;

    const permissions = rootGetters['config/permissions'];
    const trip = state.currentTripRequest;
    const isTripApproved = state.currentTripRequest.approval.requiredApprovalLevels.every(
      (ral) => ral.status === 'approved'
    );

    let readOnly = false;
    if (me.is.approver && trip.status === TRIP_STATUS.SUBMITTED) {
      readOnly = isTripApproved || permissions?.approver?.allowEditPendingApproval === false;
    }

    return readOnly;
  },

  selectedTrips(state) {
    return state.selectedTrips;
  },
  canPrintTrips(state) {
    return state.selectedTrips?.length > 0;
  },
  canRescheduleOrCancel(state, getters, rootState, rootGetters) {
    const adminRoles = ['superAdmin', 'transportationAdmin', 'limitedAdmin'];
    const me = rootGetters['user/me'];
    const currentTripRequest = getters.currentTripRequest;
    const isCurrentTripSubmittedOrDenied = getters.isCurrentTripSubmittedOrDenied;
    const { limitReschedule, limitRescheduleDays: limitRescheduleDaysString } =
      rootGetters['config/tripRequestConfig']?.display;
    const limitRescheduleDays = parseInt(limitRescheduleDaysString);
    const permissions = rootGetters['config/permissions'];

    const hasAssignments = currentTripRequest?.assignments?.length > 0 || false;
    if (!isCurrentTripSubmittedOrDenied) return false;
    if (!currentTripRequest) return false;
    // if (!hasAssignments) return true;

    if (!isTripInvoicePending(currentTripRequest)) return false;

    const rolePermissions = {};
    for (const role in me.is) {
      if (!me.is[role]) continue;

      let p = permissions[role];

      if (adminRoles.includes(role)) p = { ...p, rescheduleTrips: true };

      rolePermissions[role] = p;

      if (!rolePermissions[role]?.rescheduleTrips) continue;
      if (!currentTripRequest?.leaveDate) continue;

      if (role === 'requester') {
        if (limitReschedule) {
          const tripLeaveDate = new Date(currentTripRequest?.leaveDate);
          const now = new Date();
          const daysDiff = differenceInCalendarDays(tripLeaveDate, now);
          rolePermissions[role].rescheduleTrips = daysDiff > limitRescheduleDays;
        }
      }

      // Allow reschedule if any role of the user has rescheduleTrips = true
      if (rolePermissions[role]?.rescheduleTrips === true) return true;
    }

    return false;
  },
  cancelOrRescheduleMessage(state, getters, rootState) {
    const currentTripRequest = getters.currentTripRequest;
    let message = 'If you would like to cancel/reschedule this trip please contact your Transportation Director';

    if (rootState?.config?.tripRequestConfig?.display?.limitReschedule)
      message = rootState?.config?.tripRequestConfig?.display?.limitRescheduleMessage;
    if (!isTripInvoicePending(currentTripRequest)) message = 'Trip cannot be canceled/rescheduled';

    return message;
  },
  showApproveLevel(state, getters, rootState, rootGetters) {
    return (tripRequest) => {
      const me = rootGetters['user/me'];
      const isAdmin = rootGetters['user/isAdmin'];
      const awaitingApproval = tripRequest.approval?.awaitingApproval;

      if (!awaitingApproval) return false;

      const isPendingStatus = tripRequest.status === TRIP_STATUS.SUBMITTED;
      const hasPendingApproval = Boolean(awaitingApproval.id);

      if (isAdmin) {
        return isPendingStatus && hasPendingApproval;
      }

      const canQuickApprove = awaitingApproval.allowQuickApprove;
      const isPrimaryApprover = awaitingApproval.primaryApprovers?.some((approver) => approver.userId === me.id);

      return isPendingStatus && hasPendingApproval && canQuickApprove && me.is.approver && isPrimaryApprover;
    };
  },
};
